import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Lock, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Skeleton } from '../components/Skeleton';
import LegalDisclaimer from '../components/LegalDisclaimer';
import { trackCityGuideViewed } from '../lib/analytics';
import { extractApiError } from '../lib/apiError';
import { notifyError } from '../lib/notify';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * /relo-news/cities/:citySlug                 → full city guide
 * /relo-news/cities/:citySlug/:neighborhoodSlug → city guide scrolled to neighborhood
 *
 * Single React component handles both routes - when a neighborhood slug is
 * present we anchor-scroll to that section and emit a focused SEO title
 * "Roma Norte CDMX: Where to Live, Cost & Expat Life 2026".
 */
const CityGuide = () => {
  const { citySlug, neighborhoodSlug } = useParams();
  const [searchParams] = useSearchParams();
  const initialLang = searchParams.get('lang') === 'es' ? 'es' : 'en';
  const [lang, setLang] = useState(initialLang);
  const [city, setCity] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setCity(null);
    setNotFound(false);
    fetch(`${API_URL}/api/cities/${citySlug}${lang === 'es' ? '?lang=es' : ''}`)
      .then((r) => {
        if (r.status === 404) {
          setNotFound(true);
          return null;
        }
        return r.json();
      })
      .then((d) => {
        if (d) {
          setCity(d);
          trackCityGuideViewed({ city_slug: d.slug });
        }
      })
      .catch(() => setNotFound(true));
  }, [citySlug, lang]);

  // Scroll to the neighborhood anchor if specified.
  useEffect(() => {
    if (city && neighborhoodSlug) {
      const t = setTimeout(() => {
        const el = document.getElementById(`neighborhood-${neighborhoodSlug}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(t);
    }
  }, [city, neighborhoodSlug]);

  const focusedNeighborhood = useMemo(() => {
    if (!city || !neighborhoodSlug) return null;
    return (city.neighborhoods || []).find((n) => n.slug === neighborhoodSlug) || null;
  }, [city, neighborhoodSlug]);

  // SEO
  const seoTitle = focusedNeighborhood
    ? `${focusedNeighborhood.name} ${city?.alt_name || city?.name}: Where to Live, Cost & Expat Life 2026`
    : city
    ? `Living in ${city.name}: The Expat Guide 2026`
    : 'City Guide - Mundalo';
  const seoDescription = focusedNeighborhood
    ? `${focusedNeighborhood.name} in ${city?.name} - rent ${focusedNeighborhood.rent_range_usd}/mo. ${focusedNeighborhood.vibe}.`
    : city?.tagline || 'Mundalo city guide for Americans relocating to Mexico.';

  // Schema.org JSON-LD - city Place + every neighborhood as a sub-Place + FAQPage.
  // When focused on a neighborhood, prepend a BreadcrumbList for the deep page.
  const schema = city
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Place",
            "@id": `https://mundalo.com/relo-news/cities/${city.slug}#place`,
            "name": city.name,
            "address": {"@type": "PostalAddress", "addressRegion": city.state, "addressCountry": "MX"},
            "description": city.overview || city.tagline,
            "image": city.hero_image_url,
            "url": `https://mundalo.com/relo-news/cities/${city.slug}`,
          },
          // One Place sub-entity per neighborhood for deeper SEO surface.
          ...((city.neighborhoods || []).map((n) => ({
            "@type": "Place",
            "@id": `https://mundalo.com/relo-news/cities/${city.slug}/${n.slug}#place`,
            "name": `${n.name}, ${city.name}`,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": city.name,
              "addressRegion": city.state,
              "addressCountry": "MX",
            },
            "description": n.overview,
            "url": `https://mundalo.com/relo-news/cities/${city.slug}/${n.slug}`,
            "containedInPlace": {
              "@type": "Place",
              "@id": `https://mundalo.com/relo-news/cities/${city.slug}#place`,
            },
            // Approx rent range exposed as a SearchAction-style note.
            "additionalProperty": [{
              "@type": "PropertyValue",
              "name": "Avg monthly rent (USD)",
              "value": n.rent_range_usd,
            }],
          }))),
          city.faq && city.faq.length > 0 ? {
            "@type": "FAQPage",
            "mainEntity": city.faq.map((f) => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": {"@type": "Answer", "text": f.a},
            })),
          } : null,
          focusedNeighborhood ? {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Relo News", "item": "https://mundalo.com/relo-news"},
              {"@type": "ListItem", "position": 2, "name": "City Guides", "item": "https://mundalo.com/relo-news/cities"},
              {"@type": "ListItem", "position": 3, "name": city.name,
               "item": `https://mundalo.com/relo-news/cities/${city.slug}`},
              {"@type": "ListItem", "position": 4, "name": focusedNeighborhood.name,
               "item": `https://mundalo.com/relo-news/cities/${city.slug}/${focusedNeighborhood.slug}`},
            ],
          } : null,
        ].filter(Boolean),
      }
    : null;

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] text-[#0A1F2E]">
        <Header currentPage="news" />
        <main className="max-w-3xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="font-display italic text-3xl text-[#0A1F2E]/70 mb-4">City guide not found.</p>
          <Link
            to="/relo-news/cities"
            className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.12em] bg-[#0A1F2E] text-[#F9F6EF] px-5 py-2.5 rounded-full"
          >
            <ArrowLeft className="w-4 h-4" /> All city guides
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] text-[#0A1F2E]">
        <Header currentPage="news" />
        <main className="max-w-7xl mx-auto px-6 md:px-10 py-12 space-y-4">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6EF] text-[#0A1F2E]" data-testid="city-guide-root">
      <SEO title={seoTitle} description={seoDescription} path={`/relo-news/cities/${city.slug}${neighborhoodSlug ? `/${neighborhoodSlug}` : ''}`} />
      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
      <Header currentPage="news" />

      {/* HERO */}
      <header className="relative bg-[#0A1F2E] text-[#F9F6EF]">
        <div className="absolute inset-0">
          <img src={city.hero_image_url} alt={`${city.name} hero`}
               className="w-full h-full object-cover opacity-40" loading="eager"
               width={1200} height={800}
               onError={(e) => {
                 // eslint-disable-next-line no-console
                 console.warn(`[CityGuide] hero failed to load: ${city.slug} → ${city.hero_image_url}`);
                 e.currentTarget.style.display = 'none';
               }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F2E] via-[#0A1F2E]/60 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <nav aria-label="Breadcrumb" className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#F9F6EF]/70 mb-4">
            <Link to="/relo-news" className="hover:text-white">Relo News</Link>
            <span className="mx-2 opacity-50">→</span>
            <Link to="/relo-news/cities" className="hover:text-white">City Guides</Link>
            <span className="mx-2 opacity-50">→</span>
            <span className="text-white" data-testid="city-guide-breadcrumb-city">{city.name}</span>
            {focusedNeighborhood && (
              <>
                <span className="mx-2 opacity-50">→</span>
                <span className="text-white" data-testid="city-guide-breadcrumb-neighborhood">{focusedNeighborhood.name}</span>
              </>
            )}
          </nav>
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#E8A830] mb-3">
            {city.state}
          </p>
          <h1
            className="font-display italic text-5xl md:text-7xl text-white leading-[1.05] mb-4"
            data-testid="city-guide-headline"
          >
            {city.name}
          </h1>
          <p className="font-body font-light text-lg md:text-2xl text-[#F9F6EF]/90 max-w-3xl mb-6">
            {city.tagline}
          </p>

          {/* KEY STATS */}
          {city.stats && (
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 max-w-4xl"
              data-testid="city-guide-stats"
            >
              {[
                { label: 'Cost of living', value: `~$${city.stats.avg_cost_living_usd}/mo` },
                { label: 'Avg rent', value: `~$${city.stats.avg_rent_usd}/mo` },
                { label: 'Internet', value: `${city.stats.internet_mbps} Mbps` },
                { label: 'Expats', value: city.stats.expat_population },
              ].map((s) => (
                <div key={s.label} className="border-l-2 border-[#E8A830] pl-3">
                  <p className="font-mono uppercase text-[9px] tracking-[0.18em] text-[#F9F6EF]/65 mb-1">
                    {s.label}
                  </p>
                  <p className="font-display text-xl text-white">{s.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Lang toggle */}
          <div className="absolute right-6 md:right-10 top-6">
            <div
              className="inline-flex items-center bg-white/15 backdrop-blur rounded-full p-1 border border-white/30"
              role="tablist"
              data-testid="city-guide-lang-toggle"
            >
              {['en', 'es'].map((l) => (
                <button
                  key={l}
                  type="button"
                  role="tab"
                  aria-selected={lang === l}
                  onClick={() => setLang(l)}
                  data-testid={`city-guide-lang-${l}`}
                  className={`px-3 py-1 rounded-full font-mono uppercase text-[10px] tracking-[0.12em] transition ${
                    lang === l ? 'bg-[#E8A830] text-[#0A1F2E] font-semibold' : 'text-white/80'
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* CONTENT */}
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,260px] gap-12">

          {/* COLUMN 1 */}
          <article className="prose-mundalo max-w-none">
            <Section title="Overview">
              <p className="text-base md:text-lg leading-relaxed text-[#0A1F2E]/85 whitespace-pre-line">
                {city.overview}
              </p>
            </Section>

            {city.why_americans && city.why_americans.length > 0 && (
              <Section title="Why Americans are moving here">
                <ul className="space-y-2">
                  {city.why_americans.map((w, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="text-[#2CA8A0] mt-1">→</span>
                      <span className="text-[#0A1F2E]/85">{w}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* NEIGHBORHOODS */}
            {city.neighborhoods && city.neighborhoods.length > 0 && (
              <Section title="Neighborhood breakdown" data-testid="city-guide-neighborhoods-section">
                <div className="space-y-8">
                  {city.neighborhoods.map((n) => (
                    <div
                      key={n.slug}
                      id={`neighborhood-${n.slug}`}
                      data-testid={`neighborhood-${n.slug}`}
                      className={`border-l-2 pl-5 ${
                        focusedNeighborhood?.slug === n.slug
                          ? 'border-[#E8A830] bg-[#FFF9EC] py-4 -ml-5 pl-5 rounded-r-lg'
                          : 'border-[#EFE6CF]'
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-1">
                        <h3 className="font-display text-2xl text-[#0A1F2E]">
                          <Link to={`/relo-news/cities/${city.slug}/${n.slug}`}
                                className="hover:text-[#E8A830]">
                            {n.name}
                          </Link>
                        </h3>
                        <span className="font-mono uppercase text-[10px] tracking-[0.12em] text-[#1A5C6E] font-semibold">
                          {n.rent_range_usd}/mo
                        </span>
                      </div>
                      <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mb-2">
                        {n.vibe} · Walkability: {n.walkability}
                      </p>
                      <p className="text-[#0A1F2E]/85 leading-relaxed">{n.overview}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {city.visa_notes && (
              <Section title="Visa & legal notes">
                <LegalDisclaimer variant="legal" />
                <Prose>{city.visa_notes}</Prose>
              </Section>
            )}
            {city.healthcare && (
              <Section title="Healthcare">
                <LegalDisclaimer variant="medical" />
                <Prose>{city.healthcare}</Prose>
              </Section>
            )}
            {city.expat_community && (
              <Section title="Expat community"><Prose>{city.expat_community}</Prose></Section>
            )}

            {((city.pros && city.pros.length) || (city.cons && city.cons.length)) && (
              <Section title="Pros & cons">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {city.pros?.length > 0 && (
                    <div>
                      <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#2CA8A0] font-semibold mb-2">PROS</p>
                      <ul className="space-y-1.5">
                        {city.pros.map((p, i) => (
                          <li key={i} className="text-[#0A1F2E]/85">+ {p}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {city.cons?.length > 0 && (
                    <div>
                      <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#E8A830] font-semibold mb-2">CONS</p>
                      <ul className="space-y-1.5">
                        {city.cons.map((c, i) => (
                          <li key={i} className="text-[#0A1F2E]/85">− {c}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {city.faq && city.faq.length > 0 && (
              <Section title="Frequently asked">
                <div className="space-y-5">
                  {city.faq.map((f, i) => (
                    <details key={i} className="border-b border-[#EFE6CF] pb-4 group">
                      <summary className="cursor-pointer font-display text-lg text-[#0A1F2E] py-2">
                        {f.q}
                      </summary>
                      <p className="mt-2 text-[#0A1F2E]/80 leading-relaxed">{f.a}</p>
                    </details>
                  ))}
                </div>
              </Section>
            )}

            {city.related_cities && city.related_cities.length > 0 && (
              <Section title="You might also like">
                <div className="flex gap-3 flex-wrap">
                  {city.related_cities.map((s) => (
                    <Link
                      key={s}
                      to={`/relo-news/cities/${s}`}
                      className="inline-flex items-center gap-1 px-4 py-2 border border-[#EFE6CF] rounded-full hover:border-[#2CA8A0] text-sm transition"
                    >
                      <MapPin className="w-3.5 h-3.5 text-[#1A5C6E]" />
                      {s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </Link>
                  ))}
                </div>
              </Section>
            )}
          </article>

          {/* COLUMN 2 - neighborhood quick-jump sidebar */}
          {city.neighborhoods && city.neighborhoods.length > 0 && (
            <aside className="lg:sticky lg:top-24 self-start" data-testid="city-guide-sidebar">
              <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E] mb-3 font-semibold">
                Jump to a neighborhood
              </p>
              <ul className="space-y-1">
                {city.neighborhoods.map((n) => (
                  <li key={n.slug}>
                    <Link
                      to={`/relo-news/cities/${city.slug}/${n.slug}`}
                      className={`block py-1.5 text-sm transition ${
                        focusedNeighborhood?.slug === n.slug
                          ? 'text-[#E8A830] font-semibold'
                          : 'text-[#0A1F2E]/75 hover:text-[#0A1F2E]'
                      }`}
                      data-testid={`sidebar-nb-${n.slug}`}
                    >
                      {n.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      </main>

      <CityGuidePreview citySlug={city.slug} />

      <FullGuideCTA citySlug={city.slug} cityName={city.name} />

      <Footer />
    </div>
  );
};

// ============== FULL GUIDE 3-SECTION PREVIEW ==============
// Public preview shown above the paywall CTA. Renders Budget intro + cost
// table, City Scorecard grid, and the first 3 Housing neighborhoods. A
// gradient fade + "Want the full guide?" hint nudges purchase.
const CityGuidePreview = ({ citySlug }) => {
  const [preview, setPreview] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/cities/${citySlug}/preview`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled) return;
        setPreview(d && d.preview ? d.preview : null);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => { cancelled = true; };
  }, [citySlug]);

  if (!loaded || !preview) return null;
  const { budget, scorecard, top_neighborhoods } = preview;

  return (
    <section className="bg-white py-16 md:py-24" data-testid="city-guide-public-preview">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <p className="font-mono uppercase tracking-[0.2em] text-[10px] md:text-xs text-[#E8A830] mb-4">A peek inside</p>
        <h2 className="font-display text-3xl md:text-5xl text-[#0A1F2E] mb-10">Three sections from the full guide</h2>

        {budget?.intro && (
          <div className="mb-12" data-testid="preview-budget">
            <h3 className="font-display text-2xl md:text-3xl text-[#0A1F2E] mb-3">{budget.title || 'Budget & Finances'}</h3>
            <p className="text-base md:text-lg text-[#0A1F2E]/80 leading-relaxed mb-5">{budget.intro}</p>
            {budget.table && budget.table.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-[#EFE6CF]">
                  <thead className="bg-[#F4ECD8]">
                    <tr>
                      {Object.keys(budget.table[0]).map((k) => (
                        <th key={k} className="text-left px-3 py-2 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">{k.replace(/_/g, ' ')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {budget.table.map((row, i) => (
                      <tr key={i} className="border-t border-[#EFE6CF]">
                        {Object.values(row).map((v, j) => (
                          <td key={j} className="px-3 py-2 text-[#0A1F2E]">{typeof v === 'number' ? `$${v.toLocaleString('en-US', { timeZone: 'America/Chicago' })}` : (v || '-')}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {scorecard && Object.keys(scorecard).length > 0 && (
          <div className="mb-12 bg-[#F4ECD8] rounded-2xl p-6 md:p-8" data-testid="preview-scorecard">
            <h3 className="font-display text-2xl md:text-3xl text-[#0A1F2E] mb-5">City Scorecard</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {Object.entries(scorecard).map(([k, v]) => (
                <div key={k} className="bg-white rounded-lg p-3 border border-[#EFE6CF]">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#1A5C6E] mb-1">{k.replace(/_/g, ' ')}</p>
                  <p className="font-display text-2xl text-[#0A1F2E]">{v}<span className="text-sm text-[#0A1F2E]/40">/10</span></p>
                </div>
              ))}
            </div>
          </div>
        )}

        {top_neighborhoods && top_neighborhoods.length > 0 && (
          <div className="mb-2 relative" data-testid="preview-neighborhoods">
            <h3 className="font-display text-2xl md:text-3xl text-[#0A1F2E] mb-5">Top neighborhoods</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {top_neighborhoods.map((n, i) => (
                <div key={i} className="bg-white border border-[#EFE6CF] rounded-xl p-4">
                  <h4 className="font-display text-lg text-[#0A1F2E] mb-1">{n.name}</h4>
                  {(n.rent_low_usd || n.rent_high_usd) && (
                    <p className="text-xs font-mono text-[#2CA8A0] mb-2">${n.rent_low_usd}–${n.rent_high_usd}/mo</p>
                  )}
                  {n.vibe && <p className="text-[10px] uppercase tracking-widest text-[#1A5C6E] mb-2">{n.vibe}</p>}
                  <p className="text-sm text-[#0A1F2E]/80 line-clamp-3">{n.notes}</p>
                </div>
              ))}
            </div>
            {/* Bottom fade to suggest more content behind the paywall */}
            <div className="absolute -bottom-2 left-0 right-0 h-20 bg-gradient-to-b from-transparent to-white pointer-events-none" />
          </div>
        )}

        <p className="text-center text-sm text-[#5A6B73] mt-8">
          + 8 more sections (Safety, Healthcare, Housing, Quality of Life, Social, Education, Employment, Moving, Internet, Food) + 5 mover profiles.
        </p>
      </div>
    </section>
  );
};

// ============== FULL GUIDE CTA ==============
// Surfaces under every public /relo-news/cities/<slug> page.
// • Anonymous           → "Get Full Guide - $4.99" → /start (creates account first)
// • Free trial          → "Upgrade to unlock" → /portal/upgrade
// • Paid w/o access     → Stripe checkout for $4.99
// • Paid with access    → "View in Portal →"
// • Admin               → "View in Portal →"
const FullGuideCTA = ({ citySlug, cityName }) => {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [isAdminSession, setIsAdminSession] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);

  // Always probe /city-guides/access - this endpoint also accepts the
  // admin_token cookie, so an admin without a member access_token still
  // gets is_admin:true and we can route them to the portal instead of
  // showing a "$4.99 purchase" CTA they don't need.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/city-guides/access`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : { slugs: [], is_admin: false })
      .then((d) => {
        if (cancelled) return;
        setIsAdminSession(!!d.is_admin);
        setHasAccess(d.is_admin || (d.slugs || []).includes(citySlug));
        setAccessChecked(true);
      })
      .catch(() => setAccessChecked(true));
    return () => { cancelled = true; };
  }, [citySlug]);

  if (!accessChecked) return null;

  const isAdmin = user?.role === 'admin' || isAdminSession;
  const isFreeTrial = user?.role === 'free_trial';
  const isPaidWithoutAccess = user && !isFreeTrial && !hasAccess && !isAdmin;

  const startCheckout = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/city-guides/${citySlug}/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ success_path: `/portal/city-guides/${citySlug}`, cancel_path: `/relo-news/cities/${citySlug}` }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.already_owned) {
        window.location.href = `/portal/city-guides/${citySlug}`;
      } else if (d.url) {
        window.location.href = d.url;
      } else if (r.status === 401) {
        // Anonymous visitors hit checkout from the public city page - send them
        // to /start so they create a free trial first, then return.
        window.location.href = `/start?next=${encodeURIComponent(window.location.pathname)}`;
      } else if (r.status === 402 && d?.detail?.error === 'tier_required') {
        // Free-trial users need to upgrade before purchasing individual guides.
        window.location.href = '/portal/upgrade?reason=cityGuides';
      } else {
        notifyError(extractApiError(d, 'Could not start checkout - please try again.'));
      }
    } catch {
      notifyError('Network error - please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-[#F4ECD8] py-16 md:py-20" data-testid="full-guide-cta">
      <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
        <p className="font-mono uppercase tracking-[0.2em] text-[10px] md:text-xs text-[#1A5C6E] mb-4">The complete edition</p>
        <h2 className="font-display text-3xl md:text-5xl text-[#0A1F2E] leading-[1.1] mb-4">
          Want the complete <span className="italic">{cityName}</span> guide?
        </h2>
        <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 mb-8 max-w-xl mx-auto">
          Budget tables, neighborhood breakdowns, healthcare costs, school recommendations, the 90-day move plan, and a full scorecard across 12 dimensions.
        </p>

        {(hasAccess || isAdmin) ? (
          <Link
            to={`/portal/city-guides/${citySlug}`}
            data-testid="full-guide-view-in-portal"
            className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-medium px-7 py-3.5 rounded-full transition"
          >
            View in Portal <ArrowRight className="w-4 h-4" />
          </Link>
        ) : isFreeTrial ? (
          <Link
            to="/portal/upgrade"
            data-testid="full-guide-upgrade"
            className="inline-flex items-center gap-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-medium px-7 py-3.5 rounded-full transition"
          >
            <Lock className="w-4 h-4" /> Upgrade to unlock
          </Link>
        ) : isPaidWithoutAccess ? (
          <button
            onClick={startCheckout}
            disabled={loading}
            data-testid="full-guide-purchase"
            className="inline-flex items-center gap-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-medium px-7 py-3.5 rounded-full transition disabled:opacity-60"
          >
            {loading ? 'Opening checkout…' : <>Get Full Guide - $4.99 <ArrowRight className="w-4 h-4" /></>}
          </button>
        ) : (
          <Link
            to="/start-trial"
            data-testid="full-guide-start"
            className="inline-flex items-center gap-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-medium px-7 py-3.5 rounded-full transition"
          >
            Get Full Guide - $4.99 <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </section>
  );
};

const Section = ({ title, children, ...rest }) => (
  <section className="mb-12" {...rest}>
    <h2 className="font-display text-3xl md:text-4xl text-[#0A1F2E] mb-4">{title}</h2>
    {children}
  </section>
);

const Prose = ({ children }) => (
  <p className="text-base md:text-lg leading-relaxed text-[#0A1F2E]/85 whitespace-pre-line">
    {children}
  </p>
);

export default CityGuide;
